import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AgentTestScenarioResponse,
  CreateAgentTestScenarioRequest,
  UpdateAgentTestScenarioRequest,
} from '@troia-v3/types';

import {
  agentTrainingApi,
  type AgentQualitySnapshotResponse,
  type AgentVersionMetricsResponse,
  type ApplyTrainingRequest,
  type ApplyTrainingResponse,
  type GenerateSuggestionsResponse,
  type ListScenariosQuery,
  type ListScenariosResponse,
  type StartTrainingRequest,
  type StartTrainingResponse,
  type TestBeforeDeployComparisonResponse,
  type TestBeforeDeployRequest,
  type TestBeforeDeployResponse,
} from '../services/agentTrainingApi';

/**
 * Hooks React Query para o fluxo de treinamento de agente.
 *
 * Padrão:
 * - `useStartTraining` — mutation que dispara POST /train
 * - `useSnapshot` — query com **polling inteligente**: intervalo de 1.5s
 *   enquanto snapshot está `pending|running`, para quando `completed|failed`
 * - `useGenerateSuggestions` — mutation do Prompt Coach
 * - `useApplyTraining` — mutation que gera o novo prompt
 */

// ─── Start training ──────────────────────────────────────────────────────

export function useStartTraining(agentId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<StartTrainingResponse, Error, StartTrainingRequest>({
    mutationFn: (input: StartTrainingRequest) => {
      if (!agentId) throw new Error('agentId é obrigatório');
      return agentTrainingApi.startTraining(agentId, input);
    },
    onSuccess: (data) => {
      // Invalida listagem de snapshots do agente pra UI-4 futura
      queryClient.invalidateQueries({
        queryKey: ['agent-training', agentId, 'snapshots'],
      });
      // Semeia cache do snapshot novo pra polling pegar o estado inicial
      queryClient.setQueryData(
        ['agent-training', 'snapshot', data.snapshotId],
        null,
      );
    },
  });
}

// ─── Snapshot polling ────────────────────────────────────────────────────

/**
 * Query que faz polling do estado de um snapshot.
 *
 * `refetchInterval` é dinâmico: 1500ms enquanto running, false quando
 * completo ou erro. Isso evita loop infinito depois que termina.
 *
 * `staleTime` 5min protege queries de snapshots concluídos contra
 * refetches desnecessários (o doc é imutável após `completed`/`failed`).
 */
export function useSnapshot(snapshotId: string | undefined) {
  return useQuery<AgentQualitySnapshotResponse, Error>({
    queryKey: ['agent-training', 'snapshot', snapshotId],
    queryFn: () => {
      if (!snapshotId) throw new Error('snapshotId é obrigatório');
      return agentTrainingApi.getSnapshot(snapshotId);
    },
    enabled: !!snapshotId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 1500;
      if (data.status === 'pending' || data.status === 'running') return 1500;
      return false;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// ─── Test Before Deploy (Opção A) ────────────────────────────────────────

/**
 * Mutation que dispara o teste pré-deploy. Envia o prompt novo + model
 * opcional + tipo do teste (quick/full) pro backend.
 *
 * Se a resposta vier `fromCache: true`, o caller pode pular o polling
 * e chamar direto `useTestBeforeDeployComparison` com o `snapshotId`
 * retornado — é instantâneo.
 */
export function useTestBeforeDeploy(agentId: string | undefined) {
  return useMutation<
    TestBeforeDeployResponse,
    Error,
    TestBeforeDeployRequest
  >({
    mutationFn: (input: TestBeforeDeployRequest) => {
      if (!agentId) throw new Error('agentId é obrigatório');
      return agentTrainingApi.testBeforeDeploy(agentId, input);
    },
  });
}

/**
 * Query que busca a comparison do teste pré-deploy + snapshot final.
 * Habilitada apenas quando `snapshotId` existe E o caller passou
 * `enabled: true` (tipicamente depois do polling detectar
 * `status='completed'`).
 */
export function useTestBeforeDeployComparison(
  agentId: string | undefined,
  snapshotId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery<TestBeforeDeployComparisonResponse, Error>({
    queryKey: ['agent-training', agentId, 'test-before-deploy', snapshotId],
    queryFn: () => {
      if (!agentId || !snapshotId) {
        throw new Error('agentId e snapshotId são obrigatórios');
      }
      return agentTrainingApi.getTestBeforeDeployComparison(
        agentId,
        snapshotId,
      );
    },
    enabled: !!agentId && !!snapshotId && (options.enabled ?? false),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// ─── Latest snapshot (Coach v2 — regression banner) ────────────────────

/**
 * Busca o snapshot mais recente do agente. Usado pela `HealthTab` pra
 * exibir um banner de alerta quando o último treino detectou regressão
 * (`regressionInfo` não é null).
 *
 * StaleTime 60s — refetch a cada minuto protege contra sujeira de
 * cache mas não vira polling agressivo (snapshots mudam raramente).
 */
export function useLatestSnapshot(agentId: string | undefined) {
  return useQuery<AgentQualitySnapshotResponse | null, Error>({
    queryKey: ['agent-training', agentId, 'latest-snapshot'],
    queryFn: async () => {
      if (!agentId) throw new Error('agentId é obrigatório');
      const snapshots = await agentTrainingApi.listSnapshots(agentId, 1);
      return snapshots[0] ?? null;
    },
    enabled: !!agentId,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// ─── Generate suggestions ────────────────────────────────────────────────

export function useGenerateSuggestions(agentId: string | undefined) {
  return useMutation<GenerateSuggestionsResponse, Error, { snapshotId: string }>({
    mutationFn: ({ snapshotId }: { snapshotId: string }) => {
      if (!agentId) throw new Error('agentId é obrigatório');
      return agentTrainingApi.generateSuggestions(agentId, snapshotId);
    },
  });
}

// ─── Apply training ──────────────────────────────────────────────────────

export function useApplyTraining(agentId: string | undefined) {
  return useMutation<ApplyTrainingResponse, Error, ApplyTrainingRequest>({
    mutationFn: (input: ApplyTrainingRequest) => {
      if (!agentId) throw new Error('agentId é obrigatório');
      return agentTrainingApi.applyTraining(agentId, input);
    },
  });
}

// ─── Scenarios CRUD (Entrega 3) ──────────────────────────────────────────

/**
 * Lista cenários de teste (system + custom do tenant) aplicáveis aos
 * filtros. Usado pela ScenariosTab do AgentFormPage — ao passar
 * `agentCategory` + `agentSegment`, retorna todos os cenários que seriam
 * executados quando esse agente for treinado (universais + específicos).
 *
 * StaleTime baixo (30s) porque o cliente pode criar/editar custom
 * scenarios e quer ver a lista atualizada rapidamente. As mutations
 * também invalidam o cache via queryKey.
 */
export function useAgentScenarios(query: ListScenariosQuery = {}) {
  return useQuery<ListScenariosResponse, Error>({
    queryKey: ['agent-training', 'scenarios', query],
    queryFn: () => agentTrainingApi.listScenarios(query),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Cria um cenário customizado. Invalida o cache da listagem de cenários
 * pra todas as queries (sem tentar prever os filtros específicos usados).
 */
export function useCreateScenario() {
  const queryClient = useQueryClient();
  return useMutation<
    AgentTestScenarioResponse,
    Error,
    CreateAgentTestScenarioRequest
  >({
    mutationFn: (data: CreateAgentTestScenarioRequest) =>
      agentTrainingApi.createScenario(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agent-training', 'scenarios'],
      });
    },
  });
}

/**
 * Atualiza um cenário customizado. Invalida o cache de listagem.
 */
export function useUpdateScenario() {
  const queryClient = useQueryClient();
  return useMutation<
    AgentTestScenarioResponse,
    Error,
    { scenarioId: string; data: UpdateAgentTestScenarioRequest }
  >({
    mutationFn: ({ scenarioId, data }) =>
      agentTrainingApi.updateScenario(scenarioId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agent-training', 'scenarios'],
      });
    },
  });
}

/**
 * Soft delete de cenário customizado. Invalida o cache de listagem.
 */
export function useDeleteScenario() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (scenarioId: string) =>
      agentTrainingApi.deleteScenario(scenarioId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agent-training', 'scenarios'],
      });
    },
  });
}

// ─── Version metrics (Sprint UI-4) ───────────────────────────────────────

/**
 * Métricas agregadas dos snapshots vinculados a uma versão. Cada versão
 * tem sua própria query — usado pelo VersionsTab pra renderizar score
 * badges em cada linha. StaleTime alto (5min) porque snapshots são imutáveis.
 */
export function useVersionMetrics(
  agentId: string | undefined,
  versionId: string | undefined,
) {
  return useQuery<AgentVersionMetricsResponse, Error>({
    queryKey: ['agent-training', 'version-metrics', agentId, versionId],
    queryFn: () => {
      if (!agentId || !versionId) {
        throw new Error('agentId e versionId são obrigatórios');
      }
      return agentTrainingApi.getVersionMetrics(agentId, versionId);
    },
    enabled: !!agentId && !!versionId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
